/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Input } from '@angular/core';
import { type ColumnDef, type TableConfig, PermissionService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { type FileSystem, type HyperCdpSchedule, type Lun, type ProtectGroup } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';
import { isUndefined } from 'lodash';

import { ScheduleStrService } from '../../schedule-str.service';
import { ConfigPolicyStrService } from '../config-policy/config-policy-str.service';
import { CreateStrService } from '../create-str.service';

@Component({
    selector: 'app-hyper-cdp-schedule-create-confirm',
    templateUrl: './confirm.component.html',
    styleUrls: ['./confirm.component.scss'],
})
export class ConfirmComponent implements OnChanges, OnInit {
    @Input() type: ScheduleTypeE = ScheduleTypeE.Block;
    @Input() data: HyperCdpSchedule;
    @Input() policyValue: any;
    @Input() selectedLuns: Lun[];
    @Input() selectedPgs: ProtectGroup[];
    @Input() selectedFS: FileSystem[];
    @Input() selectedVstore: SelectOption;

    ScheduleTypeE = ScheduleTypeE;
    i18n = this.str;
    render = this.scheduleStr.render;

    isFixed: boolean;
    isDaily: boolean;
    isWeekly: boolean;
    isMonthly: boolean;
    fixedLabel: string;
    fixedRetainLabel: string;
    dayLabel: string;
    dayRetainLabel: string;
    weekLabel: string;
    weekRetainLabel: string;
    monthLabel: string;
    monthRetainLabel: string;

    isActive = true;

    PGcolumnDefs: ColumnDef[];
    LUNcolumnDefs: ColumnDef[];
    FScolumnDefs: ColumnDef[] = this.scheduleStr.filesystemColumnDefs;
    tableConfig: TableConfig;
    isUndefined = isUndefined;
    hasFsSnapshotW = false;
    hasLunSnapshotW = false;

    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    constructor(
        public util: UtilService,
        public str: CreateStrService,
        private scheduleStr: ScheduleStrService,
        public configStr: ConfigPolicyStrService,
        public permissionService: PermissionService,
    ) { }

    ngOnInit(): void {
        this.hasLunSnapshotW = this.permissionService.hasPermission('lun_snapshot_W');
        this.hasFsSnapshotW = this.permissionService.hasPermission('fs_snapshot_W');

        this.tableConfig = {
            dataKey: 'ID',
        };

        this.PGcolumnDefs = [
            {
                field: 'NAME',
                header: this.str.title.name,
                filterType: 'search',
            },
            {
                field: 'ID',
                header: this.str.title.id,
            },
        ];
        this.LUNcolumnDefs = [
            {
                field: 'NAME',
                header: this.str.title.name,
                filterType: 'search',
            },
            {
                field: 'ID',
                header: this.str.title.id,
            },
            {
                field: 'CAPACITY',
                header: this.str.title.capacity,
                columnFilter: true,
                render: (value): string => this.util.adaptUnit(value, 'Sector').label,
            },
            {
                field: 'WWN',
                header: this.str.title.wwn,
                columnFilter: true,
                render: (value): string => value || UNPLACEHOLDER,
            },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.data) {
            this.initData();
        }
    }

    initData(): void {
        if (!this.policyValue) {
            return;
        }

        this.isFixed = this.policyValue.isFixed;
        this.isDaily = this.policyValue.isDaily;
        this.isWeekly = this.policyValue.isWeekly;
        this.isMonthly = this.policyValue.isMonthly;

        if (this.isFixed) {
            const fixed = this.policyValue.fixed;
            let everyType;
            let everyNum;

            if (fixed.timeUnit === 'second') {
                everyNum = fixed.second;
                everyType = this.configStr.title.second;
            } else if (fixed.timeUnit === 'minute') {
                everyNum = fixed.minute;
                everyType = this.configStr.title.minute;
            } else if (fixed.timeUnit === 'hour') {
                everyNum = fixed.hour;
                everyType = this.configStr.title.hour;
            }

            this.fixedLabel = this.configStr.title.executePolicyTime
                .replace('{0}', everyNum)
                .replace('{1}', everyType);

            const totalSeconds = Number(this.data.FREQUENCYVALUE) * Number(this.data.FREQUENCYNUM);
            const day = Math.floor(totalSeconds / 86400);
            const hour = Math.floor((totalSeconds - day * 86400) / 3600);
            const min = Math.floor((totalSeconds - day * 86400 - hour * 3600) / 60);
            const sec = Math.floor(totalSeconds - day * 86400 - hour * 3600 - min * 60);
            if (fixed.retentionType === 'time') {
                this.fixedRetainLabel = this.configStr.title.etentionPeriodTime
                    .replace('{0}', this.data.FREQUENCYNUM)
                    .replace('{1}', String(day))
                    .replace('{2}', String(hour));
            } else {
                this.fixedRetainLabel = this.configStr.title.etentionPeriod
                    .replace('{0}', this.data.FREQUENCYNUM)
                    .replace('{1}', String(day))
                    .replace('{2}', String(hour))
                    .replace('{3}', String(min))
                    .replace('{4}', String(sec));
            }
        }

        if (this.isDaily) {
            const daily = this.policyValue.daily;
            const minuteLabel = daily.minute < 10 ?
                '0' + daily.minute :
                daily.minute;
            const dayLabel = daily.hours
                .map((item) => (item < 10 ? '0' + item : item) + ':' + minuteLabel).join(', ');
            this.dayLabel = this.configStr.title.executePolicyDay.replace('{0}', dayLabel);
            this.dayRetainLabel = this.configStr.title.retainNum + ' ' + daily.retentionNum;
        }

        if (this.isWeekly) {
            const weekly = this.policyValue.weekly;
            const weekDay = weekly.days
                .map((item) => this.configStr.dateOpt.find((a) => a.value === item).label).join(', ');
            this.weekLabel = this.configStr.title.executePolicyWeek
                .replace('{0}', weekDay)
                .replace('{1}', weekly.time);
            this.weekRetainLabel = this.configStr.title.retainNum + ' ' + weekly.retentionNum;
        }

        if (this.isMonthly) {
            const monthly = this.policyValue.monthly;
            const dates = monthly.days
                .map((item) => item === 99 ? this.configStr.title.lastday : item).join(', ');

            this.monthLabel = this.configStr.title.executePolicyMonth
                .replace('{0}', dates)
                .replace('{1}', monthly.time);
            this.monthRetainLabel = this.configStr.title.retainNum + ' ' + monthly.retentionNum;
        }
    }
}
